import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import './CellPicker.css';

/**
 * Tap-any-cell editor for a single Par or SI value. No auto-advance — sets the
 * one value and closes. SI duplicates are allowed (selectable even if used);
 * the grid surfaces the conflict separately.
 *
 * `overlayZIndex` because this portals to document.body: opened from inside
 * another modal the shared `.modal-overlay` class (defined in several page
 * stylesheets, at 3000/4000/100000 depending on which loaded last) sits above
 * this sheet's own 1200. The caller that stacks it says how high.
 */
const CellPicker = ({ type, holeNumber, value, strokeIndices = [], currentHoleIdx, onSelect, onClear, onClose, overlayZIndex, unit }) => {
  // A LENGTH is not a choice from a short list the way a par or a stroke index
  // is — it is an arbitrary two or three digit number, so it gets a keypad
  // rather than a grid. Held locally and committed on Done, because a yardage
  // is only meaningful once all of its digits are in.
  const [draft, setDraft] = useState('');
  const isLength = type === 'length';

  useEffect(() => {
    if (isLength) setDraft(value === null || value === undefined ? '' : String(value));
  }, [isLength, value, holeNumber]);

  const commitLength = () => {
    const trimmed = draft.trim();
    if (trimmed === '') onClear();
    else onSelect(Number(trimmed));
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // 3, 4 and 5 — the same three the quick-fill keypad offers, and the same
  // three the server's PAR_TYPES splits stats by. A par 6 exists on a handful
  // of courses worldwide and none this app models; offering it here made a
  // mis-tap on a par 5 cheaper than it should be.
  const parOptions = [3, 4, 5];

  return ReactDOM.createPortal(
    <div className="cp-overlay" style={overlayZIndex ? { zIndex: overlayZIndex } : undefined} onClick={onClose}>
      <div className="cp-sheet" onClick={e => e.stopPropagation()}>
        <div className="cp-header">
          <span className="cp-title">
            Hole {holeNumber} · {type === 'par' ? 'Par' : isLength ? `Length${unit ? ` (${unit})` : ''}` : 'Stroke index'}
          </span>
          <button className="cp-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        {isLength ? (
          <div className="cp-length">
            <div className="cp-length-readout">{draft || '—'}</div>
            <div className="cp-grid cp-grid--keypad">
              {['1','2','3','4','5','6','7','8','9','0'].map(d => (
                <button
                  key={d}
                  className="cp-btn"
                  onClick={() => setDraft(prev => (prev + d).replace(/^0+(?=\d)/, '').slice(0, 4))}
                >
                  {d}
                </button>
              ))}
              <button
                className="cp-btn cp-btn--back"
                onClick={() => setDraft(prev => prev.slice(0, -1))}
                aria-label="Backspace"
              >
                ⌫
              </button>
            </div>
          </div>
        ) : type === 'par' ? (
          <div className="cp-grid cp-grid--par">
            {parOptions.map(n => (
              <button
                key={n}
                className={`cp-btn${Number(value) === n ? ' cp-btn--selected' : ''}`}
                onClick={() => onSelect(n)}
              >
                {n}
              </button>
            ))}
          </div>
        ) : (
          <div className="cp-grid cp-grid--si">
            {Array.from({ length: 18 }, (_, i) => i + 1).map(n => {
              const isSelected = Number(value) === n;
              const usedElsewhere = !isSelected && strokeIndices.some(
                (si, idx) => idx !== currentHoleIdx && Number(si) === n
              );
              return (
                <button
                  key={n}
                  className={`cp-btn${isSelected ? ' cp-btn--selected' : ''}${usedElsewhere ? ' cp-btn--used' : ''}`}
                  onClick={() => onSelect(n)}
                >
                  {n}
                </button>
              );
            })}
          </div>
        )}

        <div className="cp-footer">
          <button
            className="cp-clear"
            onClick={isLength ? () => { setDraft(''); onClear(); } : onClear}
            disabled={isLength ? draft === '' : (value === '' || value === null || value === undefined)}
          >
            Clear
          </button>
          <button
            className="cp-done"
            onClick={isLength ? () => { commitLength(); onClose(); } : onClose}
          >
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CellPicker;
